/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import {
  Shield,
  ShieldCheck,
  AlertTriangle,
  HeartPulse,
  AlertOctagon,
  Flame,
  CheckCircle2,
  Calendar,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit2,
  Trash2,
  Camera,
  X,
  Clock,
  MapPin,
  User,
  Building2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Upload,
  Sparkles,
  Info,
  Layers,
  FileCheck2,
  Activity
} from 'lucide-react';
import { CostCenter, SafetyIncident, SafetyClassification, IncidentPhoto } from '../types.ts';
import { generateSafetyIncidentPDF, generateMonthlySafetyCrossPDF } from '../utils/safetyPdfGenerator.ts';

interface SafetyCrossManagerProps {
  activeCostCenter: CostCenter | null;
  incidents: SafetyIncident[];
  onSaveIncident: (incident: SafetyIncident) => void;
  onDeleteIncident: (incidentId: string) => void;
}

const MONTHS_SPANISH = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function SafetyCrossManager({
  activeCostCenter,
  incidents,
  onSaveIncident,
  onDeleteIncident
}: SafetyCrossManagerProps) {
  // Navigation & Period State
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(7); // 7 = Agosto (0-indexed)
  const [viewMode, setViewMode] = useState<'calendar' | 'cross'>('calendar');

  // Search & Filter State for Historial
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'accidents' | 'near_miss' | 'first_aid' | 'environmental'>('all');

  // Modal States
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedIncidentForDetail, setSelectedIncidentForDetail] = useState<SafetyIncident | null>(null);

  // Form State
  const [formIncident, setFormIncident] = useState<Partial<SafetyIncident>>({});
  const [photoPreviews, setPhotoPreviews] = useState<IncidentPhoto[]>([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isGeneratingMonthlyPdf, setIsGeneratingMonthlyPdf] = useState(false);

  const monthName = MONTHS_SPANISH[selectedMonthIndex];
  const formattedMonthYear = `${monthName} ${selectedYear}`;
  const costCenterTitle = activeCostCenter ? activeCostCenter.name : 'Sede Bajío - Salamanca';

  // Days count in current month (e.g. 31 in August)
  const daysInCurrentMonth = useMemo(() => {
    return new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();
  }, [selectedYear, selectedMonthIndex]);

  // Helper to determine if a day is in the future
  const isDateInFuture = (day: number) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed
    const currentDay = today.getDate();

    if (selectedYear > currentYear) return true;
    if (selectedYear < currentYear) return false;
    if (selectedMonthIndex > currentMonth) return true;
    if (selectedMonthIndex < currentMonth) return false;
    return day > currentDay;
  };

  // Filter incidents for this cost center and month
  const monthIncidents = useMemo(() => {
    const monthStr = String(selectedMonthIndex + 1).padStart(2, '0');
    const yearMonthPrefix = `${selectedYear}-${monthStr}`;

    return incidents.filter((inc) => {
      const matchCostCenter = !activeCostCenter || inc.costCenterId === activeCostCenter.id || inc.costCenterCode === activeCostCenter.code;
      const matchDate = inc.date.startsWith(yearMonthPrefix);
      return matchCostCenter && matchDate;
    });
  }, [incidents, activeCostCenter, selectedYear, selectedMonthIndex]);

  // Calendar days mapping with isFuture awareness
  const calendarDays = useMemo(() => {
    const days: { day: number; incident?: SafetyIncident; isFuture: boolean }[] = [];
    for (let d = 1; d <= daysInCurrentMonth; d++) {
      const found = monthIncidents.find((inc) => inc.day === d);
      const isFuture = isDateInFuture(d);
      days.push({ day: d, incident: found, isFuture });
    }
    return days;
  }, [daysInCurrentMonth, monthIncidents, selectedYear, selectedMonthIndex]);

  // Monthly Metrics Calculation (Normative STPS / ISO 45001)
  const metrics = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    let elapsedDaysInMonth = daysInCurrentMonth;
    if (selectedYear > currentYear || (selectedYear === currentYear && selectedMonthIndex > currentMonth)) {
      elapsedDaysInMonth = 0;
    } else if (selectedYear === currentYear && selectedMonthIndex === currentMonth) {
      elapsedDaysInMonth = Math.min(daysInCurrentMonth, currentDay);
    }

    const totalMonth = monthIncidents.length;
    const nearMissCount = monthIncidents.filter(i => i.classification === 'near_miss').length;
    const firstAidCount = monthIncidents.filter(i => i.classification === 'first_aid').length;
    const medTreatmentCount = monthIncidents.filter(i => i.classification === 'medical_treatment').length;
    const lostTimeCount = monthIncidents.filter(i => i.classification === 'lost_time').length;
    const envCount = monthIncidents.filter(i => i.classification === 'environmental').length;
    
    // Total adverse events that occurred in elapsed days
    const adverseEventsInElapsed = monthIncidents.filter(i => {
      const isAdverse = i.classification !== 'safe';
      return isAdverse && i.day <= elapsedDaysInMonth;
    }).length;

    const totalAdverseEvents = nearMissCount + firstAidCount + medTreatmentCount + lostTimeCount + envCount;
    
    // All elapsed days without adverse incident are SAFE DAYS (Verde por defecto)
    const safeDaysCount = Math.max(0, elapsedDaysInMonth - adverseEventsInElapsed);
    const pendingDaysCount = Math.max(0, daysInCurrentMonth - elapsedDaysInMonth);
    
    // Total lost days this month
    const totalLostDays = monthIncidents.reduce((sum, i) => sum + (Number(i.lostDays) || 0), 0);

    // Days without lost time incidents
    let daysWithoutIncident = 342;
    if (lostTimeCount > 0) {
      daysWithoutIncident = 0;
    } else if (totalAdverseEvents > 0) {
      daysWithoutIncident = Math.max(12, 342 - (totalAdverseEvents * 2));
    }

    return {
      totalMonth,
      elapsedDaysInMonth,
      pendingDaysCount,
      safeDaysCount,
      nearMissCount,
      firstAidCount,
      medTreatmentCount,
      lostTimeCount,
      envCount,
      totalAdverseEvents,
      totalLostDays,
      daysWithoutIncident
    };
  }, [monthIncidents, daysInCurrentMonth, selectedYear, selectedMonthIndex]);

  // Historial filtered list
  const filteredHistorial = useMemo(() => {
    return monthIncidents.filter((inc) => {
      // Classification filter
      if (filterType === 'accidents') {
        if (inc.classification !== 'lost_time' && inc.classification !== 'medical_treatment') return false;
      } else if (filterType === 'near_miss' && inc.classification !== 'near_miss') {
        return false;
      } else if (filterType === 'first_aid' && inc.classification !== 'first_aid') {
        return false;
      } else if (filterType === 'environmental' && inc.classification !== 'environmental') {
        return false;
      }

      // Search term
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchTitle = inc.title?.toLowerCase().includes(term);
        const matchPerson = inc.affectedPersonName?.toLowerCase().includes(term);
        const matchArea = inc.area?.toLowerCase().includes(term);
        const matchId = inc.id?.toLowerCase().includes(term);
        const matchDesc = inc.description?.toLowerCase().includes(term);
        const matchSupervisor = inc.supervisorName?.toLowerCase().includes(term);
        return matchTitle || matchPerson || matchArea || matchId || matchDesc || matchSupervisor;
      }

      return true;
    });
  }, [monthIncidents, filterType, searchTerm]);

  // Open form modal for a new incident or clicked day
  const handleOpenDayModal = (day: number, existingIncident?: SafetyIncident) => {
    const monthStr = String(selectedMonthIndex + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateStr = `${selectedYear}-${monthStr}-${dayStr}`;

    if (existingIncident) {
      setFormIncident({ ...existingIncident });
      setPhotoPreviews(existingIncident.photos || []);
    } else {
      setFormIncident({
        id: `INC-${selectedYear}-${monthStr}-D${dayStr}`,
        costCenterId: activeCostCenter?.id || 'CC-MAZDA-BAJIO',
        costCenterName: activeCostCenter?.name || 'Sede Bajío - Mazda Planta Salamanca',
        costCenterCode: activeCostCenter?.code || 'CC-MAZDA-BAJIO',
        date: dateStr,
        day: day,
        time: '10:00',
        shift: 'Turno 1 (Matutino)',
        area: activeCostCenter?.name || 'Línea de Decapado Químico y Térmico',
        classification: 'safe',
        severity: 'Baja',
        affectedPersonName: '',
        affectedPersonRole: 'Operador Especialista',
        affectedPersonEmployeeId: '',
        supervisorName: activeCostCenter?.manager || 'Ing. Supervisor HSEQ',
        seniority: '1 año',
        affectedBodyPart: 'N/A',
        injuryType: 'Ninguna (Sin Lesión)',
        medicalAttention: 'Ninguna / Sin Lesión',
        lostDays: 0,
        ppeUsed: 'Careta facial, guantes de nitrilo 18", mandil químico y botas dieléctricas',
        title: `Jornada del día ${day} de ${monthName}`,
        description: 'Operación desarrollada bajo condiciones seguras y cumplimiento de protocolos STPS.',
        unsafeAct: 'Ninguno detectado.',
        unsafeCondition: 'Ninguna detectada.',
        rootCause: 'Operación estándar sin desviaciones.',
        immediateAction: 'Monitoreo continuo de EPP y parámetros de seguridad.',
        preventiveAction: 'Plática de 5 minutos al inicio del turno.',
        actionOwner: activeCostCenter?.manager || 'Supervisor de Turno',
        actionDeadline: dateStr,
        actionStatus: 'Implementada',
        photos: []
      });
      setPhotoPreviews([]);
    }
    setIsFormModalOpen(true);
  };

  // Handle Photo Upload (up to 5 photos)
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (photoPreviews.length >= 5) {
      alert('Se ha alcanzado el límite máximo de 5 fotografías por incidencia.');
      return;
    }

    setIsUploadingPhoto(true);
    const remainingSlots = 5 - photoPreviews.length;
    const filesToProcess = (Array.from(files) as File[]).slice(0, remainingSlots);

    const promises = filesToProcess.map((file: File) => {
      return new Promise<IncidentPhoto>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target?.result as string;
          resolve({
            id: `PHT-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
            name: file.name,
            dataUrl: base64,
            caption: `Evidencia fotográfica: ${file.name.replace(/\.[^/.]+$/, '')}`,
            uploadedAt: new Date().toISOString()
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(promises)
      .then((newPhotos) => {
        setPhotoPreviews((prev) => [...prev, ...newPhotos]);
        setIsUploadingPhoto(false);
      })
      .catch((err) => {
        console.error('Error al cargar imágenes:', err);
        alert('Ocurrió un error al procesar las imágenes.');
        setIsUploadingPhoto(false);
      });
  };

  const handleRemovePhoto = (photoId: string) => {
    setPhotoPreviews((prev) => prev.filter((p) => p.id !== photoId));
  };

  const handleUpdatePhotoCaption = (photoId: string, caption: string) => {
    setPhotoPreviews((prev) =>
      prev.map((p) => (p.id === photoId ? { ...p, caption } : p))
    );
  };

  // Submit Incident Form
  const handleSubmitIncident = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formIncident.title || !formIncident.date) {
      alert('Por favor complete los campos obligatorios.');
      return;
    }

    const dayNum = formIncident.day || parseInt(formIncident.date.split('-')[2]) || 1;
    const finalIncident: SafetyIncident = {
      id: formIncident.id || `INC-${selectedYear}-${String(selectedMonthIndex + 1).padStart(2, '0')}-D${String(dayNum).padStart(2, '0')}`,
      costCenterId: formIncident.costCenterId || activeCostCenter?.id || 'CC-MAZDA-BAJIO',
      costCenterName: formIncident.costCenterName || activeCostCenter?.name || 'Sede Bajío',
      costCenterCode: formIncident.costCenterCode || activeCostCenter?.code || 'CC-MAZDA-BAJIO',
      date: formIncident.date,
      day: dayNum,
      time: formIncident.time || '10:00',
      shift: formIncident.shift || 'Turno 1 (Matutino)',
      area: formIncident.area || 'Línea de Hornos y Decapado',
      classification: formIncident.classification || 'safe',
      severity: formIncident.severity || 'Baja',
      affectedPersonName: formIncident.affectedPersonName || (formIncident.classification === 'safe' ? 'N/A' : 'Personal Operativo'),
      affectedPersonRole: formIncident.affectedPersonRole || 'Operador',
      affectedPersonEmployeeId: formIncident.affectedPersonEmployeeId || 'ISN-OP-00',
      supervisorName: formIncident.supervisorName || activeCostCenter?.manager || 'Supervisor HSEQ',
      seniority: formIncident.seniority || '1 año',
      affectedBodyPart: formIncident.affectedBodyPart || 'N/A',
      injuryType: formIncident.injuryType || 'Ninguna',
      medicalAttention: formIncident.medicalAttention || 'Ninguna / Sin Lesión',
      lostDays: Number(formIncident.lostDays) || 0,
      ppeUsed: formIncident.ppeUsed || 'Careta, guantes de nitrilo, botas de seguridad',
      title: formIncident.title,
      description: formIncident.description || '',
      unsafeAct: formIncident.unsafeAct || '',
      unsafeCondition: formIncident.unsafeCondition || '',
      rootCause: formIncident.rootCause || '',
      immediateAction: formIncident.immediateAction || '',
      preventiveAction: formIncident.preventiveAction || '',
      actionOwner: formIncident.actionOwner || 'Supervisor HSEQ',
      actionDeadline: formIncident.actionDeadline || formIncident.date,
      actionStatus: formIncident.actionStatus || 'Implementada',
      photos: photoPreviews,
      registeredBy: formIncident.registeredBy || 'Supervisor de Seguridad',
      createdAt: formIncident.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSaveIncident(finalIncident);
    setIsFormModalOpen(false);
  };

  // Download Full Monthly Safety Cross Official PDF (STPS/ISO Compliant)
  const handleDownloadMonthlyPDF = async () => {
    try {
      setIsGeneratingMonthlyPdf(true);
      await generateMonthlySafetyCrossPDF({
        costCenterName: activeCostCenter ? activeCostCenter.name : 'Sede Bajío - Salamanca',
        costCenterCode: activeCostCenter ? activeCostCenter.code : 'CC-BAJ-01',
        year: selectedYear,
        monthIndex: selectedMonthIndex,
        monthName: MONTHS_SPANISH[selectedMonthIndex],
        incidents: monthIncidents,
        calendarDays,
        metrics: {
          daysWithoutIncident: metrics.daysWithoutIncident,
          safeDaysCount: metrics.safeDaysCount,
          elapsedDaysInMonth: metrics.elapsedDaysInMonth,
          pendingDaysCount: metrics.pendingDaysCount,
          nearMissCount: metrics.nearMissCount,
          firstAidCount: metrics.firstAidCount,
          medTreatmentCount: metrics.medTreatmentCount,
          lostTimeCount: metrics.lostTimeCount,
          envCount: metrics.envCount,
          totalMonth: metrics.totalMonth,
          totalLostDays: metrics.totalLostDays
        }
      });
    } catch (error) {
      console.error('Error al generar PDF de Cruz de Seguridad:', error);
    } finally {
      setIsGeneratingMonthlyPdf(false);
    }
  };

  // Helper for status background & badge
  // If incident exists: show classification color
  // If no incident and day is FUTURE: show neutral/gray "Por Transcurrir" (DO NOT SHOW GREEN)
  // If no incident and day is PAST/TODAY: show VERDE / DÍA SEGURO
  const getClassificationBadge = (classification?: SafetyClassification, isFuture: boolean = false) => {
    if (classification) {
      switch (classification) {
        case 'near_miss':
          return {
            label: 'Casi Incidente / Condición',
            sublabel: 'Riesgo Detectado',
            bg: 'bg-amber-950/80 text-amber-200 border-amber-500 hover:border-amber-300 hover:bg-amber-900/90 ring-1 ring-amber-500/50 shadow-amber-950/30',
            badgeBg: 'bg-amber-950/80 text-amber-300 border-amber-700',
            dot: 'bg-amber-400',
            icon: AlertTriangle
          };
        case 'first_aid':
          return {
            label: 'Primeros Auxilios',
            sublabel: 'Atención en Planta',
            bg: 'bg-blue-950/80 text-blue-200 border-blue-500 hover:border-blue-300 hover:bg-blue-900/90 ring-1 ring-blue-500/50 shadow-blue-950/30',
            badgeBg: 'bg-blue-950/80 text-blue-300 border-blue-700',
            dot: 'bg-blue-400',
            icon: HeartPulse
          };
        case 'medical_treatment':
          return {
            label: 'Accidente s/ Tiempo Perdido',
            sublabel: 'Atención Médica',
            bg: 'bg-orange-950/80 text-orange-200 border-orange-500 hover:border-orange-300 hover:bg-orange-900/90 ring-1 ring-orange-500/50 shadow-orange-950/30',
            badgeBg: 'bg-orange-950/80 text-orange-300 border-orange-700',
            dot: 'bg-orange-400',
            icon: AlertOctagon
          };
        case 'lost_time':
          return {
            label: 'Accidente Incapacitante (LTI)',
            sublabel: 'Días Perdidos',
            bg: 'bg-red-950/90 text-red-100 border-red-500 hover:border-red-400 hover:bg-red-900/90 ring-1 ring-red-500 shadow-red-950/50',
            badgeBg: 'bg-red-950/80 text-red-300 border-red-700',
            dot: 'bg-red-400',
            icon: Flame
          };
        case 'environmental':
          return {
            label: 'Incidente Ambiental',
            sublabel: 'Derrame / Emisión',
            bg: 'bg-purple-950/80 text-purple-200 border-purple-500 hover:border-purple-300 hover:bg-purple-900/90 ring-1 ring-purple-500/50 shadow-purple-950/30',
            badgeBg: 'bg-purple-950/80 text-purple-300 border-purple-700',
            dot: 'bg-purple-400',
            icon: AlertTriangle
          };
        case 'safe':
        default:
          return {
            label: 'Día Seguro / Sin Novedad',
            sublabel: 'Operación Segura',
            bg: 'bg-emerald-950/70 text-emerald-200 border-emerald-600/70 hover:border-emerald-400 hover:bg-emerald-900/80 shadow-emerald-950/20',
            badgeBg: 'bg-emerald-950/80 text-emerald-300 border-emerald-700',
            dot: 'bg-emerald-400',
            icon: CheckCircle2
          };
      }
    }

    // No incident recorded
    if (isFuture) {
      return {
        label: 'Por Transcurrir',
        sublabel: 'Día Pendiente',
        bg: 'bg-[#141519]/90 text-slate-400 border-[#252733] hover:border-slate-500 hover:bg-[#1b1c24] opacity-85',
        badgeBg: 'bg-[#181920] text-slate-400 border-[#282a36]',
        dot: 'bg-slate-500',
        icon: Clock
      };
    }

    return {
      label: 'Día Seguro / Sin Novedad',
      sublabel: 'Operación Segura',
      bg: 'bg-emerald-950/70 text-emerald-200 border-emerald-600/70 hover:border-emerald-400 hover:bg-emerald-900/80 shadow-emerald-950/20',
      badgeBg: 'bg-emerald-950/80 text-emerald-300 border-emerald-700',
      dot: 'bg-emerald-400',
      icon: CheckCircle2
    };
  };

  // Traditional 31-cell Industrial Safety Cross Matrix
  const crossLayout = [
    [null, null, 1, 2, 3, null, null],
    [null, null, 4, 5, 6, null, null],
    [7, 8, 9, 10, 11, 12, 13],
    [14, 15, 16, 17, 18, 19, 20],
    [21, 22, 23, 24, 25, 26, 27],
    [null, null, 28, 29, 30, null, null],
    [null, null, null, 31, null, null, null],
  ];

  return (
    <div className="space-y-6 text-slate-100">
      
      {/* 1. TOP HEADER & MONTH SELECTOR */}
      <div className="bg-[#16171b] p-5 rounded-2xl border border-[#282932] flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-emerald-950/70 border border-emerald-700/80 text-emerald-400">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-bold text-white tracking-tight">Cruz de Seguridad Industrial</h3>
            <span className="bg-[#22242c] text-slate-300 border border-[#323440] text-[11px] font-mono px-2.5 py-0.5 rounded-md">
              NOM-019-STPS / ISO 45001
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Monitoreo diario de incidentes, actos inseguros y primeros auxilios para <strong className="text-white">{costCenterTitle}</strong>. Los días transcurridos sin reporte se marcan en <span className="text-emerald-400 font-semibold">Verde (Día Seguro)</span> y los días futuros permanecen neutros.
          </p>
        </div>

        {/* Month Selector & Action */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center bg-[#101114] rounded-xl border border-[#272832] p-1 shadow-inner">
            <button
              onClick={() => {
                if (selectedMonthIndex === 0) {
                  setSelectedMonthIndex(11);
                  setSelectedYear(prev => prev - 1);
                } else {
                  setSelectedMonthIndex(prev => prev - 1);
                }
              }}
              className="p-1.5 hover:bg-[#20222a] rounded-lg text-slate-400 hover:text-white transition-colors cursor-pointer"
              title="Mes Anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 text-xs font-bold font-mono text-white min-w-[110px] text-center">
              {formattedMonthYear}
            </span>
            <button
              onClick={() => {
                if (selectedMonthIndex === 11) {
                  setSelectedMonthIndex(0);
                  setSelectedYear(prev => prev + 1);
                } else {
                  setSelectedMonthIndex(prev => prev + 1);
                }
              }}
              className="p-1.5 hover:bg-[#20222a] rounded-lg text-slate-400 hover:text-white transition-colors cursor-pointer"
              title="Mes Siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Download Official Monthly PDF Button */}
          <button
            onClick={handleDownloadMonthlyPDF}
            disabled={isGeneratingMonthlyPdf}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-xs font-bold text-white transition-all cursor-pointer shadow-md disabled:opacity-50"
            title="Descargar Reporte Mensual de Cruz de Seguridad Industrial en PDF"
          >
            <Download className="h-4 w-4" />
            <span>{isGeneratingMonthlyPdf ? 'Generando PDF...' : 'Descargar Reporte PDF'}</span>
          </button>

          <button
            onClick={() => handleOpenDayModal(new Date().getDate())}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#e52e2e] hover:bg-[#ff3d3d] text-xs font-bold text-white transition-all cursor-pointer shadow-md"
          >
            <Plus className="h-4 w-4" />
            <span>Registrar Incidencia</span>
          </button>
        </div>
      </div>

      {/* 2. REAL-TIME METRICS CARDS */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-[#141519] p-3.5 rounded-xl border border-[#24252c] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1.5 h-full bg-emerald-500"></div>
          <span className="text-[10px] uppercase font-mono text-[#8b919e] block font-semibold">Días Sin Incidentes</span>
          <span className="text-2xl font-bold text-emerald-400 mt-1 block font-mono">{metrics.daysWithoutIncident}</span>
          <span className="text-[10px] text-slate-500 mt-0.5 block truncate">Cero accidentes LTI</span>
        </div>

        <div className="bg-[#141519] p-3.5 rounded-xl border border-[#24252c]">
          <span className="text-[10px] uppercase font-mono text-[#8b919e] block font-semibold">Días Seguros Mes</span>
          <span className="text-2xl font-bold text-emerald-400 mt-1 block font-mono">
            {metrics.safeDaysCount} <span className="text-xs text-slate-500 font-normal">/ {metrics.elapsedDaysInMonth} transcurridos</span>
          </span>
          <span className="text-[10px] text-slate-500 mt-0.5 block">{metrics.pendingDaysCount} días por transcurrir</span>
        </div>

        <div className="bg-[#141519] p-3.5 rounded-xl border border-[#24252c]">
          <span className="text-[10px] uppercase font-mono text-[#8b919e] block font-semibold">Casi Incidentes</span>
          <span className="text-2xl font-bold text-amber-400 mt-1 block font-mono">{metrics.nearMissCount}</span>
          <span className="text-[10px] text-slate-500 mt-0.5 block">Condiciones / Actos</span>
        </div>

        <div className="bg-[#141519] p-3.5 rounded-xl border border-[#24252c]">
          <span className="text-[10px] uppercase font-mono text-[#8b919e] block font-semibold">Primeros Auxilios</span>
          <span className="text-2xl font-bold text-blue-400 mt-1 block font-mono">{metrics.firstAidCount}</span>
          <span className="text-[10px] text-slate-500 mt-0.5 block">Atención en planta</span>
        </div>

        <div className="bg-[#141519] p-3.5 rounded-xl border border-[#24252c]">
          <span className="text-[10px] uppercase font-mono text-[#8b919e] block font-semibold">Accidentes (LTI)</span>
          <span className="text-2xl font-bold text-red-400 mt-1 block font-mono">{metrics.lostTimeCount}</span>
          <span className="text-[10px] text-slate-500 mt-0.5 block">{metrics.totalLostDays} días perdidos</span>
        </div>

        <div className="bg-[#141519] p-3.5 rounded-xl border border-[#24252c]">
          <span className="text-[10px] uppercase font-mono text-[#8b919e] block font-semibold">Total Eventos Mes</span>
          <span className="text-2xl font-bold text-white mt-1 block font-mono">{metrics.totalMonth}</span>
          <span className="text-[10px] text-slate-500 mt-0.5 block">Registrados en bitácora</span>
        </div>
      </div>

      {/* 3. VISUAL CALENDAR & CRUZ DE SEGURIDAD */}
      <div className="bg-[#16171b] p-5 sm:p-6 rounded-2xl border border-[#282932] space-y-5">
        
        {/* Calendar Legend & View Switcher */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#282932] pb-4">
          <div className="flex items-center gap-3">
            <h4 className="text-sm font-bold text-slate-200">
              Distribución Diaria — {formattedMonthYear}
            </h4>
            <span className="text-xs text-slate-400 hidden sm:inline">
              (Haga clic en cualquier día para registrar o editar incidencia)
            </span>
          </div>

          {/* View Toggle (Calendar / Industrial Cross) */}
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-[#101114] p-1 rounded-xl border border-[#272832]">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  viewMode === 'calendar'
                    ? 'bg-slate-700 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Calendar className="h-3.5 w-3.5" />
                <span>Vista Calendario</span>
              </button>
              <button
                onClick={() => setViewMode('cross')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  viewMode === 'cross'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>Cruz Industrial</span>
              </button>
            </div>
          </div>
        </div>

        {/* VIEW MODE 1: CALENDAR GRID */}
        {viewMode === 'calendar' ? (
          <div className="grid grid-cols-4 sm:grid-cols-7 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-11 gap-2.5">
            {calendarDays.map(({ day, incident, isFuture }) => {
              const badge = getClassificationBadge(incident?.classification, isFuture);
              const Icon = badge.icon;
              const hasPhotos = incident?.photos && incident.photos.length > 0;

              return (
                <button
                  key={day}
                  onClick={() => handleOpenDayModal(day, incident)}
                  title={
                    incident
                      ? `${incident.title} (${badge.label})`
                      : isFuture
                      ? `Día ${day} - Por transcurrir (Clic para registrar novedad anticipada)`
                      : `Día ${day} - Día Seguro (Clic para registrar novedad)`
                  }
                  className={`p-2.5 rounded-xl border transition-all duration-150 flex flex-col justify-between h-22 text-left group cursor-pointer relative overflow-hidden ${
                    badge.bg
                  } hover:scale-[1.03] hover:shadow-lg`}
                >
                  <div className="flex justify-between items-center w-full">
                    <span className="font-mono font-black text-sm">
                      {day}
                    </span>
                    <Icon className="h-4 w-4 shrink-0" />
                  </div>

                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[9px] font-sans font-bold block truncate leading-tight">
                      {badge.label}
                    </span>
                    
                    <p className="text-[8px] text-slate-300/80 truncate font-mono">
                      {incident?.area ? incident.area.split('-')[0] : isFuture ? 'Día Pendiente' : 'Jornada Segura'}
                    </p>
                  </div>

                  {/* Photo Badge Indicator */}
                  {hasPhotos && (
                    <div className="absolute top-1.5 right-6 flex items-center gap-0.5 bg-black/60 px-1 py-0.5 rounded text-[8px] font-mono text-amber-300 border border-amber-500/40">
                      <Camera className="h-2.5 w-2.5" />
                      <span>{incident.photos.length}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          /* VIEW MODE 2: TRADITIONAL INDUSTRIAL SAFETY CROSS (31 CELLS) */
          <div className="flex flex-col items-center justify-center py-4">
            <div className="grid grid-cols-7 gap-2 sm:gap-3 max-w-md sm:max-w-xl w-full">
              {crossLayout.flat().map((dayNum, idx) => {
                if (dayNum === null) {
                  return <div key={`empty-${idx}`} className="w-full aspect-square" />;
                }

                if (dayNum > daysInCurrentMonth) {
                  return (
                    <div
                      key={`inactive-${dayNum}`}
                      className="w-full aspect-square rounded-xl bg-[#121317] border border-[#22232a] flex items-center justify-center text-slate-600 font-mono text-xs opacity-40"
                    >
                      {dayNum}
                    </div>
                  );
                }

                const inc = monthIncidents.find(i => i.day === dayNum);
                const isFuture = isDateInFuture(dayNum);
                const badge = getClassificationBadge(inc?.classification, isFuture);
                const Icon = badge.icon;
                const hasPhotos = inc?.photos && inc.photos.length > 0;

                return (
                  <button
                    key={`cross-day-${dayNum}`}
                    onClick={() => handleOpenDayModal(dayNum, inc)}
                    title={
                      inc
                        ? `Día ${dayNum}: ${inc.title} (${badge.label})`
                        : isFuture
                        ? `Día ${dayNum}: Por transcurrir`
                        : `Día ${dayNum}: Día Seguro (Sin Incidentes)`
                    }
                    className={`w-full aspect-square p-1.5 sm:p-2 rounded-xl border transition-all duration-150 flex flex-col justify-between text-left group cursor-pointer relative ${
                      badge.bg
                    } hover:scale-105 hover:shadow-xl`}
                  >
                    <div className="flex justify-between items-center w-full">
                      <span className="font-mono font-black text-xs sm:text-sm">{dayNum}</span>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                    </div>
                    <span className="text-[8px] font-bold block truncate leading-tight">
                      {badge.label.split('/')[0]}
                    </span>
                    {hasPhotos && (
                      <div className="absolute bottom-1 right-1 bg-black/70 px-1 rounded text-[7px] font-mono text-amber-300">
                        📷
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-slate-400 mt-4 text-center">
              Cruz de Seguridad Industrial Oficial: 31 casillas estandarizadas. Las casillas en verde indican jornadas transcurridas con operación segura; casillas neutras indican días pendientes del mes.
            </p>

            {/* Direct Official PDF Download Box in Industrial Cross View */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3.5 sm:p-4 bg-[#121317] rounded-xl border border-[#272832] mt-5 w-full max-w-xl shadow-md">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-emerald-950/80 border border-emerald-600/60 text-emerald-400">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-white">Reporte Mensual Cruz de Seguridad (PDF)</p>
                  <p className="text-[10px] text-slate-400">Incluye matriz gráfica, KPIs de siniestralidad y firmas Comisión Mixta NOM-019-STPS</p>
                </div>
              </div>
              <button
                onClick={handleDownloadMonthlyPDF}
                disabled={isGeneratingMonthlyPdf}
                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap shadow-md disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                <span>{isGeneratingMonthlyPdf ? 'Generando...' : 'Descargar PDF Oficial'}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 4. NORMATIVE COLOR CODE TABLE (CUADRO DE COLORES DE ACUERDO A LA NORMA DE SEGURIDAD) */}
      <div className="bg-[#16171b] p-5 sm:p-6 rounded-2xl border border-[#282932] space-y-4 shadow-md">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#282932] pb-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            <div>
              <h4 className="text-sm sm:text-base font-bold text-white tracking-wide">
                Código de Colores Oficial de Seguridad Industrial
              </h4>
              <p className="text-xs text-slate-400">
                Criterios de clasificación técnica conforme a normas STPS, ISO 45001 y SEMARNAT
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono bg-emerald-950/80 text-emerald-300 border border-emerald-700/60 px-2.5 py-1 rounded-md self-start sm:self-auto">
            NOM-019-STPS / NOM-021-STPS
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 pt-1">
          {/* Verde */}
          <div className="p-3.5 rounded-xl bg-emerald-950/40 border border-emerald-600/70 flex items-start gap-3 transition-all hover:bg-emerald-950/60">
            <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center text-slate-950 shrink-0 shadow-sm shadow-emerald-500/30">
              <CheckCircle2 className="h-5 w-5 text-slate-950 stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-xs text-emerald-300">VERDE — Día Seguro</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-emerald-900/80 text-emerald-200 border border-emerald-700 rounded">Sin Novedad</span>
              </div>
              <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                Jornada laboral sin accidentes ni desviaciones. Día en verde por defecto en la Cruz de Seguridad Industrial.
              </p>
            </div>
          </div>

          {/* Amarillo */}
          <div className="p-3.5 rounded-xl bg-amber-950/40 border border-amber-500/70 flex items-start gap-3 transition-all hover:bg-amber-950/60">
            <div className="w-9 h-9 rounded-lg bg-amber-400 flex items-center justify-center text-slate-950 shrink-0 shadow-sm shadow-amber-400/30">
              <AlertTriangle className="h-5 w-5 text-slate-950 stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-xs text-amber-300">AMARILLO — Casi Incidente</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-900/80 text-amber-200 border border-amber-700 rounded">NOM-019-STPS</span>
              </div>
              <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                Condición o acto inseguro detectado oportunamente. Evento sin lesión que pudo derivar en incidente.
              </p>
            </div>
          </div>

          {/* Azul */}
          <div className="p-3.5 rounded-xl bg-blue-950/40 border border-blue-500/70 flex items-start gap-3 transition-all hover:bg-blue-950/60">
            <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center text-white shrink-0 shadow-sm shadow-blue-500/30">
              <HeartPulse className="h-5 w-5 text-white stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-xs text-blue-300">AZUL — Primeros Auxilios</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-blue-900/80 text-blue-200 border border-blue-700 rounded">NOM-005-STPS</span>
              </div>
              <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                Lesión menor atendida en botiquín o servicio médico de planta (curación leve) sin requerir incapacidad.
              </p>
            </div>
          </div>

          {/* Naranja */}
          <div className="p-3.5 rounded-xl bg-orange-950/40 border border-orange-500/70 flex items-start gap-3 transition-all hover:bg-orange-950/60">
            <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center text-white shrink-0 shadow-sm shadow-orange-500/30">
              <AlertOctagon className="h-5 w-5 text-white stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-xs text-orange-300">NARANJA — Accidente s/ T. Perdido</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-orange-900/80 text-orange-200 border border-orange-700 rounded">NOM-021-STPS</span>
              </div>
              <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                Lesión con atención médica externa pero sin generar días de incapacidad laboral o tiempo perdido.
              </p>
            </div>
          </div>

          {/* Rojo */}
          <div className="p-3.5 rounded-xl bg-red-950/40 border border-red-500/70 flex items-start gap-3 transition-all hover:bg-red-950/60">
            <div className="w-9 h-9 rounded-lg bg-red-600 flex items-center justify-center text-white shrink-0 shadow-sm shadow-red-600/30">
              <Flame className="h-5 w-5 text-white stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-xs text-red-300">ROJO — Accidente Incapacitante (LTI)</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-red-900/80 text-red-200 border border-red-700 rounded">STPS Formato CM-2A</span>
              </div>
              <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                Accidente con pérdida de jornadas de trabajo (Lost Time Injury). Reinicia el contador de días sin incidentes.
              </p>
            </div>
          </div>

          {/* Morado */}
          <div className="p-3.5 rounded-xl bg-purple-950/40 border border-purple-500/70 flex items-start gap-3 transition-all hover:bg-purple-950/60">
            <div className="w-9 h-9 rounded-lg bg-purple-600 flex items-center justify-center text-white shrink-0 shadow-sm shadow-purple-600/30">
              <AlertTriangle className="h-5 w-5 text-white stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-xs text-purple-300">MORADO — Incidente Ambiental</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-purple-900/80 text-purple-200 border border-purple-700 rounded">SEMARNAT / PROFEPA</span>
              </div>
              <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                Derrame químico, fuga, emisión no controlada o impacto ambiental en instalaciones de planta.
              </p>
            </div>
          </div>

          {/* Gris / Neutro (Por Transcurrir) */}
          <div className="p-3.5 rounded-xl bg-[#141519]/80 border border-[#282a36] flex items-start gap-3 transition-all hover:bg-[#191b22]">
            <div className="w-9 h-9 rounded-lg bg-[#22242f] border border-[#353846] flex items-center justify-center text-slate-400 shrink-0 shadow-sm">
              <Clock className="h-5 w-5 text-slate-300 stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-xs text-slate-300">NEUTRO / GRIS — Por Transcurrir</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-[#20222a] text-slate-400 border border-[#323544] rounded">Día Futuro</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-1 leading-snug">
                Días restantes del mes que aún no han ocurrido. Permanecen neutros y se evalúan conforme transcurre la jornada.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 5. HISTORIAL DE INCIDENCIAS (BOTTOM SECTION WITH EDIT, DELETE, DOWNLOAD PDF) */}
      <div className="bg-[#16171b] p-5 sm:p-6 rounded-2xl border border-[#282932] space-y-5">
        
        {/* Historial Header & Filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#282932] pb-4">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#e52e2e]" />
              <h4 className="text-base font-bold text-white">Historial de Incidencias y Reportes de Seguridad</h4>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Registro documental con análisis de causa raíz, acciones correctivas (CAPA) y descarga de reportes PDF oficiales.
            </p>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-2.5 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar por colaborador, área, folio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#101114] border border-[#2c2e3a] rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-[#e52e2e]"
              />
            </div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1 rounded-lg border font-medium cursor-pointer transition-all ${
              filterType === 'all'
                ? 'bg-[#e52e2e] text-white border-[#e52e2e]'
                : 'bg-[#1a1b22] text-slate-400 border-[#2b2d38] hover:text-white'
            }`}
          >
            Todos ({monthIncidents.length})
          </button>
          <button
            onClick={() => setFilterType('accidents')}
            className={`px-3 py-1 rounded-lg border font-medium cursor-pointer transition-all ${
              filterType === 'accidents'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-[#1a1b22] text-slate-400 border-[#2b2d38] hover:text-white'
            }`}
          >
            Accidentes ({metrics.lostTimeCount + metrics.medTreatmentCount})
          </button>
          <button
            onClick={() => setFilterType('near_miss')}
            className={`px-3 py-1 rounded-lg border font-medium cursor-pointer transition-all ${
              filterType === 'near_miss'
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-[#1a1b22] text-slate-400 border-[#2b2d38] hover:text-white'
            }`}
          >
            Casi Incidentes ({metrics.nearMissCount})
          </button>
          <button
            onClick={() => setFilterType('first_aid')}
            className={`px-3 py-1 rounded-lg border font-medium cursor-pointer transition-all ${
              filterType === 'first_aid'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-[#1a1b22] text-slate-400 border-[#2b2d38] hover:text-white'
            }`}
          >
            Primeros Auxilios ({metrics.firstAidCount})
          </button>
          <button
            onClick={() => setFilterType('environmental')}
            className={`px-3 py-1 rounded-lg border font-medium cursor-pointer transition-all ${
              filterType === 'environmental'
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-[#1a1b22] text-slate-400 border-[#2b2d38] hover:text-white'
            }`}
          >
            Ambientales ({metrics.envCount})
          </button>
        </div>

        {/* Historial Cards List */}
        {filteredHistorial.length === 0 ? (
          <div className="p-10 text-center rounded-xl bg-[#121317] border border-[#24252f] space-y-2">
            <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto" />
            <h5 className="font-bold text-sm text-slate-200">No hay incidencias registradas con los filtros seleccionados</h5>
            <p className="text-xs text-slate-500">
              Operación con estándar de seguridad íntegro para {formattedMonthYear}.
            </p>
          </div>
        ) : (
          <div className="space-y-3.5">
            {filteredHistorial.map((inc) => {
              const badge = getClassificationBadge(inc.classification);
              const hasPhotos = inc.photos && inc.photos.length > 0;

              return (
                <div
                  key={inc.id}
                  className="bg-[#121317] p-4.5 rounded-xl border border-[#262732] hover:border-[#3a3c4c] transition-all space-y-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#20212b] pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono font-bold text-xs text-[#e52e2e] bg-[#22242e] px-2 py-0.5 rounded border border-[#2f313e]">
                        {inc.id}
                      </span>
                      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded border ${badge.bg}`}>
                        {badge.label}
                      </span>
                      <span className="text-[10px] font-mono text-slate-400 bg-[#1c1d24] px-2 py-0.5 rounded border border-[#272834]">
                        Severidad: {inc.severity}
                      </span>
                      {hasPhotos && (
                        <span className="text-[10px] font-mono text-amber-400 bg-amber-950/60 border border-amber-800/80 px-2 py-0.5 rounded flex items-center gap-1">
                          <Camera className="h-3 w-3" />
                          {inc.photos.length} Fotos
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {inc.date} ({inc.time} hrs)
                      </span>
                      <span>•</span>
                      <span>{inc.shift}</span>
                    </div>
                  </div>

                  {/* Body Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div className="space-y-1">
                      <h5 className="font-bold text-sm text-white leading-snug">{inc.title}</h5>
                      <p className="text-slate-400 line-clamp-2">{inc.description}</p>
                    </div>

                    <div className="space-y-1 bg-[#181920] p-2.5 rounded-lg border border-[#242530]">
                      <div className="text-[10px] font-mono text-slate-400 uppercase font-semibold">Personal Involucrado:</div>
                      <div className="font-bold text-slate-200">{inc.affectedPersonName}</div>
                      <div className="text-[11px] text-slate-400">{inc.affectedPersonRole} ({inc.affectedPersonEmployeeId})</div>
                      <div className="text-[10px] text-[#e52e2e]">Supervisor: {inc.supervisorName}</div>
                    </div>

                    <div className="space-y-1 bg-[#181920] p-2.5 rounded-lg border border-[#242530]">
                      <div className="text-[10px] font-mono text-slate-400 uppercase font-semibold">Acción Correctiva (CAPA):</div>
                      <p className="text-[11px] text-slate-300 line-clamp-2">{inc.preventiveAction || inc.immediateAction}</p>
                      <div className="flex items-center justify-between text-[10px] font-mono pt-1 text-slate-400">
                        <span>Resp: {inc.actionOwner}</span>
                        <span className="text-emerald-400 font-bold">{inc.actionStatus}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-[#20212b]">
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <MapPin className="h-3.5 w-3.5 text-[#e52e2e]" />
                      <span>{inc.area}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Download Official PDF */}
                      <button
                        onClick={() => generateSafetyIncidentPDF(inc)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#20222a] hover:bg-[#282a34] text-xs font-bold text-emerald-400 border border-emerald-900/60 transition-all cursor-pointer shadow-xs"
                        title="Descargar Reporte"
                      >
                        <Download className="h-3.5 w-3.5" />
                        <span>Descargar PDF</span>
                      </button>

                      {/* View Details */}
                      <button
                        onClick={() => {
                          setSelectedIncidentForDetail(inc);
                          setIsDetailModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#20222a] hover:bg-[#282a34] text-xs font-medium text-slate-200 border border-[#303240] transition-all cursor-pointer shadow-xs"
                      >
                        <Eye className="h-3.5 w-3.5 text-blue-400" />
                        <span>Ver Detalle</span>
                      </button>

                      {/* Edit */}
                      <button
                        onClick={() => handleOpenDayModal(inc.day, inc)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#20222a] hover:bg-[#282a34] text-xs font-medium text-amber-300 border border-[#303240] transition-all cursor-pointer shadow-xs"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                        <span>Editar</span>
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => {
                          if (window.confirm(`¿Está seguro de eliminar el reporte de incidencia ${inc.id}?`)) {
                            onDeleteIncident(inc.id);
                          }
                        }}
                        className="p-1.5 rounded-lg bg-[#20222a] hover:bg-red-950 text-slate-400 hover:text-red-400 border border-[#303240] transition-all cursor-pointer shadow-xs"
                        title="Eliminar Incidencia"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 5. INCIDENT REGISTRATION & EDIT MODAL */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-[#16171c] border border-[#2f313e] rounded-2xl max-w-4xl w-full p-6 space-y-5 shadow-2xl text-slate-100 my-8 max-h-[90vh] overflow-y-auto">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-[#282935] pb-3.5">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-[#e52e2e]/20 text-[#e52e2e] border border-[#e52e2e]/40">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-white">
                    {formIncident.id ? `Registro de Seguridad — Folio ${formIncident.id}` : 'Registrar Incidencia de Seguridad'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Estándar de investigación NOM-019-STPS / NOM-021-STPS / ISO 45001
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsFormModalOpen(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-[#242530] cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitIncident} className="space-y-4.5 text-xs">
              
              {/* SECTION A: CLASIFICACIÓN Y SEVERIDAD */}
              <div className="space-y-2 bg-[#101115] p-3.5 rounded-xl border border-[#262732]">
                <label className="block text-slate-300 font-bold uppercase font-mono text-[10px]">
                  1. Clasificación del Evento y Nivel de Severidad *
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'safe', label: '🟢 Día Seguro / Sin Novedad', desc: 'Jornada normal de trabajo' },
                    { id: 'near_miss', label: '🟡 Casi Incidente / Condición Insegura', desc: 'No hubo lesión personal' },
                    { id: 'first_aid', label: '🔵 Primeros Auxilios / Menor', desc: 'Curación en botiquín de planta' },
                    { id: 'medical_treatment', label: '🟠 Accidente Sin Tiempo Perdido', desc: 'Atención médica sin incapacidad' },
                    { id: 'lost_time', label: '🔴 Accidente Incapacitante (LTI)', desc: 'Con días de tiempo perdido' },
                    { id: 'environmental', label: '🟣 Incidente Ambiental / Derrame', desc: 'Afectación a suelo o drenaje' }
                  ].map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => setFormIncident({ ...formIncident, classification: item.id as SafetyClassification })}
                      className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all ${
                        formIncident.classification === item.id
                          ? 'bg-[#22242e] border-[#e52e2e] text-white ring-1 ring-[#e52e2e]'
                          : 'bg-[#181920] border-[#292a36] text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <div className="font-bold text-[11px] text-white">{item.label}</div>
                      <div className="text-[9px] text-slate-500 mt-0.5">{item.desc}</div>
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Nivel de Severidad</label>
                    <select
                      value={formIncident.severity || 'Baja'}
                      onChange={(e) => setFormIncident({ ...formIncident, severity: e.target.value as any })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    >
                      <option value="Baja">Baja (Leve)</option>
                      <option value="Media">Media (Moderada)</option>
                      <option value="Alta">Alta (Grave)</option>
                      <option value="Crítica">Crítica (Muy Grave)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Fecha del Evento *</label>
                    <input
                      type="date"
                      required
                      value={formIncident.date || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, date: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Hora Exacta</label>
                    <input
                      type="time"
                      value={formIncident.time || '10:00'}
                      onChange={(e) => setFormIncident({ ...formIncident, time: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Turno</label>
                    <select
                      value={formIncident.shift || 'Turno 1 (Matutino)'}
                      onChange={(e) => setFormIncident({ ...formIncident, shift: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    >
                      <option value="Turno 1 (Matutino)">Turno 1 (Matutino)</option>
                      <option value="Turno 2 (Vespertino)">Turno 2 (Vespertino)</option>
                      <option value="Turno 3 (Nocturno)">Turno 3 (Nocturno)</option>
                      <option value="Mixto / Administrativo">Mixto / Administrativo</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SECTION B: DATOS DEL PERSONAL Y UBICACIÓN */}
              <div className="space-y-2 bg-[#101115] p-3.5 rounded-xl border border-[#262732]">
                <label className="block text-slate-300 font-bold uppercase font-mono text-[10px]">
                  2. Personal Involucrado y Ubicación en Planta
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Colaborador Afectado</label>
                    <input
                      type="text"
                      placeholder="Nombre completo del operador..."
                      value={formIncident.affectedPersonName || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, affectedPersonName: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Puesto / Categoría</label>
                    <input
                      type="text"
                      placeholder="Ej: Operador de Hornos Pirolíticos"
                      value={formIncident.affectedPersonRole || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, affectedPersonRole: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">No. Nómina / ID</label>
                    <input
                      type="text"
                      placeholder="Ej: ISN-OP-104"
                      value={formIncident.affectedPersonEmployeeId || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, affectedPersonEmployeeId: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Área / Zona Específica en Planta</label>
                    <input
                      type="text"
                      placeholder="Ej: Tinas de Ácido #2, Horno 1, Patio Maniobras..."
                      value={formIncident.area || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, area: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Supervisor a Cargo / Reportante</label>
                    <input
                      type="text"
                      value={formIncident.supervisorName || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, supervisorName: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION C: LESIÓN Y ATENCIÓN MÉDICA */}
              {formIncident.classification !== 'safe' && (
                <div className="space-y-2 bg-[#101115] p-3.5 rounded-xl border border-[#262732]">
                  <label className="block text-slate-300 font-bold uppercase font-mono text-[10px]">
                    3. Detalle de Lesión y Atención Médica
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-slate-400 font-medium mb-1">Parte del Cuerpo</label>
                      <input
                        type="text"
                        placeholder="Ej: Manos / Ojos / Espalda..."
                        value={formIncident.affectedBodyPart || ''}
                        onChange={(e) => setFormIncident({ ...formIncident, affectedBodyPart: e.target.value })}
                        className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 font-medium mb-1">Naturaleza de la Lesión</label>
                      <input
                        type="text"
                        placeholder="Ej: Quemadura, Corte, Golpe..."
                        value={formIncident.injuryType || ''}
                        onChange={(e) => setFormIncident({ ...formIncident, injuryType: e.target.value })}
                        className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 font-medium mb-1">Tipo de Atención</label>
                      <select
                        value={formIncident.medicalAttention || 'Botiquín Interno'}
                        onChange={(e) => setFormIncident({ ...formIncident, medicalAttention: e.target.value })}
                        className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                      >
                        <option value="Botiquín Interno">Botiquín Interno</option>
                        <option value="Servicio Médico de Planta">Servicio Médico de Planta</option>
                        <option value="IMSS / Hospital">IMSS / Hospital Externo</option>
                        <option value="Ninguna / Sin Lesión">Ninguna / Sin Lesión</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 font-medium mb-1">Días de Incapacidad (LTI)</label>
                      <input
                        type="number"
                        min="0"
                        value={formIncident.lostDays ?? 0}
                        onChange={(e) => setFormIncident({ ...formIncident, lostDays: parseInt(e.target.value) || 0 })}
                        className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                      />
                    </div>
                  </div>
                  <div className="pt-1">
                    <label className="block text-slate-400 font-medium mb-1">EPP en Uso al Momento del Evento</label>
                    <input
                      type="text"
                      placeholder="Ej: Careta facial, guantes de nitrilo 18in, botas con casquillo dieléctrico..."
                      value={formIncident.ppeUsed || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, ppeUsed: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                </div>
              )}

              {/* SECTION D: DESCRIPCIÓN Y ANÁLISIS CAUSAL (5 PORQUÉS) */}
              <div className="space-y-2 bg-[#101115] p-3.5 rounded-xl border border-[#262732]">
                <label className="block text-slate-300 font-bold uppercase font-mono text-[10px]">
                  4. Descripción Circunstanciada y Análisis de Causa Raíz (STPS)
                </label>
                <div>
                  <label className="block text-slate-400 font-medium mb-1">Título Resumen del Evento *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Fuga menor de solución neutralizante en charola de tina #2..."
                    value={formIncident.title || ''}
                    onChange={(e) => setFormIncident({ ...formIncident, title: e.target.value })}
                    className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 font-medium mb-1">Descripción Detallada de los Hechos (Qué, Cómo, Cuándo)</label>
                  <textarea
                    rows={2}
                    placeholder="Detallar cronología de los acontecimientos y condiciones ambientales..."
                    value={formIncident.description || ''}
                    onChange={(e) => setFormIncident({ ...formIncident, description: e.target.value })}
                    className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                  <div>
                    <label className="block text-red-400 font-medium mb-1">Acto Inseguro Detectado</label>
                    <textarea
                      rows={2}
                      placeholder="Describir acción humana insegura..."
                      value={formIncident.unsafeAct || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, unsafeAct: e.target.value })}
                      className="w-full bg-[#181920] border border-red-950/60 rounded-lg p-2 text-white focus:outline-none focus:border-red-500 text-[11px]"
                    />
                  </div>
                  <div>
                    <label className="block text-amber-400 font-medium mb-1">Condición Insegura Detectada</label>
                    <textarea
                      rows={2}
                      placeholder="Describir falla de equipo o entorno..."
                      value={formIncident.unsafeCondition || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, unsafeCondition: e.target.value })}
                      className="w-full bg-[#181920] border border-amber-950/60 rounded-lg p-2 text-white focus:outline-none focus:border-amber-500 text-[11px]"
                    />
                  </div>
                  <div>
                    <label className="block text-emerald-400 font-medium mb-1">Causa Raíz Conclusiva</label>
                    <textarea
                      rows={2}
                      placeholder="Análisis 5 porqués conclusivo..."
                      value={formIncident.rootCause || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, rootCause: e.target.value })}
                      className="w-full bg-[#181920] border border-emerald-950/60 rounded-lg p-2 text-white focus:outline-none focus:border-emerald-500 text-[11px]"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION E: PLAN DE ACCIONES CORRECTIVAS Y PREVENTIVAS (CAPA) */}
              <div className="space-y-2 bg-[#101115] p-3.5 rounded-xl border border-[#262732]">
                <label className="block text-slate-300 font-bold uppercase font-mono text-[10px]">
                  5. Plan de Acciones Correctivas y Preventivas (CAPA)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Acción Inmediata de Contención</label>
                    <textarea
                      rows={2}
                      placeholder="Acción tomada en el momento para mitigar el riesgo..."
                      value={formIncident.immediateAction || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, immediateAction: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Acción Correctiva / Preventiva Definitiva</label>
                    <textarea
                      rows={2}
                      placeholder="Modificación a procedimiento, ingeniería o capacitación..."
                      value={formIncident.preventiveAction || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, preventiveAction: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Responsable de Implementación</label>
                    <input
                      type="text"
                      value={formIncident.actionOwner || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, actionOwner: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Fecha Compromiso</label>
                    <input
                      type="date"
                      value={formIncident.actionDeadline || ''}
                      onChange={(e) => setFormIncident({ ...formIncident, actionDeadline: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-medium mb-1">Estatus CAPA</label>
                    <select
                      value={formIncident.actionStatus || 'Implementada'}
                      onChange={(e) => setFormIncident({ ...formIncident, actionStatus: e.target.value as any })}
                      className="w-full bg-[#181920] border border-[#2f313e] rounded-lg p-2 text-white focus:outline-none focus:border-[#e52e2e]"
                    >
                      <option value="Implementada">Implementada</option>
                      <option value="En Proceso">En Proceso</option>
                      <option value="Pendiente">Pendiente</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SECTION F: EVIDENCIA FOTOGRÁFICA (HASTA 5 FOTOS) */}
              <div className="space-y-3 bg-[#101115] p-3.5 rounded-xl border border-[#262732]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Camera className="h-4 w-4 text-amber-400" />
                    <label className="text-slate-300 font-bold uppercase font-mono text-[10px]">
                      6. Evidencia Fotográfica ({photoPreviews.length} / 5 Fotografías)
                    </label>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono">
                    Formatos JPG, PNG (Hasta 5 fotos para reporte oficial PDF)
                  </span>
                </div>

                {/* Upload Trigger Box */}
                {photoPreviews.length < 5 && (
                  <label className="border-2 border-dashed border-[#2f313e] hover:border-[#e52e2e] rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer bg-[#14151b] hover:bg-[#1a1b24] transition-all text-center group">
                    <Upload className="h-6 w-6 text-slate-400 group-hover:text-[#e52e2e] transition-colors" />
                    <div>
                      <span className="font-semibold text-white text-xs block">
                        Haga clic para agregar fotos o arrastre y suelte aquí
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {5 - photoPreviews.length} espacios disponibles
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                )}

                {/* Uploaded Photos Grid */}
                {photoPreviews.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                    {photoPreviews.map((photo, idx) => (
                      <div
                        key={photo.id}
                        className="bg-[#181920] p-2 rounded-xl border border-[#282a36] space-y-2 relative group"
                      >
                        <div className="relative h-28 w-full rounded-lg overflow-hidden bg-black/40 flex items-center justify-center">
                          <img
                            src={photo.dataUrl}
                            alt={photo.name}
                            className="h-full w-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(photo.id)}
                            className="absolute top-1.5 right-1.5 p-1 bg-red-600/90 hover:bg-red-600 text-white rounded-md transition-all cursor-pointer shadow-md"
                            title="Eliminar foto"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          <span className="absolute bottom-1.5 left-1.5 bg-black/70 px-1.5 py-0.5 rounded text-[9px] font-mono text-slate-200">
                            Foto #{idx + 1}
                          </span>
                        </div>
                        <input
                          type="text"
                          placeholder="Pie de foto / descripción..."
                          value={photo.caption || ''}
                          onChange={(e) => handleUpdatePhotoCaption(photo.id, e.target.value)}
                          className="w-full bg-[#101114] border border-[#2b2d38] rounded-md px-2 py-1 text-[10px] text-white placeholder-slate-500 focus:outline-none focus:border-[#e52e2e]"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#282932]">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-[#20222a] hover:bg-[#282a34] text-slate-300 cursor-pointer font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-[#e52e2e] hover:bg-[#ff3d3d] text-white font-bold cursor-pointer shadow-md transition-all flex items-center gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Guardar Registro de Incidencia</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* 6. INCIDENT DETAIL VIEW MODAL */}
      {isDetailModalOpen && selectedIncidentForDetail && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-[#16171c] border border-[#2f313e] rounded-2xl max-w-3xl w-full p-6 space-y-5 shadow-2xl text-slate-100 my-8 max-h-[90vh] overflow-y-auto">
            
            <div className="flex justify-between items-start border-b border-[#282935] pb-3.5">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-xs text-[#e52e2e] bg-[#22242e] px-2 py-0.5 rounded border border-[#2f313e]">
                    {selectedIncidentForDetail.id}
                  </span>
                  <span className={`text-xs font-bold px-2.5 py-0.5 rounded border ${getClassificationBadge(selectedIncidentForDetail.classification).bg}`}>
                    {getClassificationBadge(selectedIncidentForDetail.classification).label}
                  </span>
                </div>
                <h3 className="font-bold text-lg text-white mt-1.5">{selectedIncidentForDetail.title}</h3>
                <p className="text-xs text-slate-400">
                  {selectedIncidentForDetail.date} ({selectedIncidentForDetail.time} hrs) • {selectedIncidentForDetail.shift} • {selectedIncidentForDetail.area}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => generateSafetyIncidentPDF(selectedIncidentForDetail)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#e52e2e] hover:bg-[#ff3d3d] text-xs font-bold text-white transition-all cursor-pointer shadow-md"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>Descargar PDF</span>
                </button>
                <button
                  onClick={() => setIsDetailModalOpen(false)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-[#242530] cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="space-y-4 text-xs">
              
              {/* Description */}
              <div className="bg-[#101115] p-3.5 rounded-xl border border-[#262732] space-y-1">
                <h5 className="font-bold text-slate-400 uppercase font-mono text-[10px]">Descripción Circunstanciada:</h5>
                <p className="text-slate-200 leading-relaxed">{selectedIncidentForDetail.description}</p>
              </div>

              {/* Personal & Medical Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-[#101115] p-3.5 rounded-xl border border-[#262732] space-y-1.5">
                  <h5 className="font-bold text-slate-400 uppercase font-mono text-[10px]">Personal Involucrado:</h5>
                  <div className="text-white font-bold">{selectedIncidentForDetail.affectedPersonName}</div>
                  <div className="text-slate-400">{selectedIncidentForDetail.affectedPersonRole} ({selectedIncidentForDetail.affectedPersonEmployeeId})</div>
                  <div className="text-slate-400">Supervisor: <strong className="text-white">{selectedIncidentForDetail.supervisorName}</strong></div>
                  <div className="text-slate-400">EPP: {selectedIncidentForDetail.ppeUsed}</div>
                </div>

                <div className="bg-[#101115] p-3.5 rounded-xl border border-[#262732] space-y-1.5">
                  <h5 className="font-bold text-slate-400 uppercase font-mono text-[10px]">Evaluación Médica y Severidad:</h5>
                  <div className="text-slate-200">Parte Afectada: <strong className="text-white">{selectedIncidentForDetail.affectedBodyPart}</strong></div>
                  <div className="text-slate-200">Lesión: <strong className="text-white">{selectedIncidentForDetail.injuryType}</strong></div>
                  <div className="text-slate-200">Atención: {selectedIncidentForDetail.medicalAttention}</div>
                  <div className="text-slate-200">Días de Incapacidad: <strong className="text-red-400 font-mono">{selectedIncidentForDetail.lostDays} días</strong></div>
                </div>
              </div>

              {/* Root Cause Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-red-950/30 border border-red-900/50 p-3 rounded-xl space-y-1">
                  <span className="text-[10px] uppercase font-mono font-bold text-red-400 block">Acto Inseguro:</span>
                  <p className="text-slate-300 text-[11px]">{selectedIncidentForDetail.unsafeAct || 'N/A'}</p>
                </div>
                <div className="bg-amber-950/30 border border-amber-900/50 p-3 rounded-xl space-y-1">
                  <span className="text-[10px] uppercase font-mono font-bold text-amber-400 block">Condición Insegura:</span>
                  <p className="text-slate-300 text-[11px]">{selectedIncidentForDetail.unsafeCondition || 'N/A'}</p>
                </div>
                <div className="bg-emerald-950/30 border border-emerald-900/50 p-3 rounded-xl space-y-1">
                  <span className="text-[10px] uppercase font-mono font-bold text-emerald-400 block">Causa Raíz (5 Porqués):</span>
                  <p className="text-slate-300 text-[11px]">{selectedIncidentForDetail.rootCause || 'N/A'}</p>
                </div>
              </div>

              {/* CAPA Action */}
              <div className="bg-[#101115] p-3.5 rounded-xl border border-[#262732] space-y-2">
                <h5 className="font-bold text-slate-400 uppercase font-mono text-[10px]">Plan de Acción Correctiva (CAPA):</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="text-slate-400 block font-semibold text-[10px]">Acción Inmediata:</span>
                    <p className="text-slate-200">{selectedIncidentForDetail.immediateAction}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-semibold text-[10px]">Acción Preventiva Definitiva:</span>
                    <p className="text-slate-200">{selectedIncidentForDetail.preventiveAction}</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-[#20212b] flex items-center justify-between text-slate-400 font-mono text-[11px]">
                  <span>Responsable: {selectedIncidentForDetail.actionOwner}</span>
                  <span>Compromiso: {selectedIncidentForDetail.actionDeadline}</span>
                  <span className="text-emerald-400 font-bold">Estatus: {selectedIncidentForDetail.actionStatus}</span>
                </div>
              </div>

              {/* Photos Gallery */}
              {selectedIncidentForDetail.photos && selectedIncidentForDetail.photos.length > 0 && (
                <div className="space-y-2 bg-[#101115] p-3.5 rounded-xl border border-[#262732]">
                  <h5 className="font-bold text-slate-400 uppercase font-mono text-[10px]">
                    Evidencia Fotográfica ({selectedIncidentForDetail.photos.length} Fotos):
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedIncidentForDetail.photos.map((photo, idx) => (
                      <div key={photo.id} className="bg-[#181920] p-2 rounded-xl border border-[#282a36] space-y-1.5">
                        <div className="h-32 rounded-lg overflow-hidden bg-black/40 flex items-center justify-center">
                          <img src={photo.dataUrl} alt={photo.name} className="h-full w-full object-cover" />
                        </div>
                        <p className="text-[10px] text-slate-300 truncate font-medium">
                          Foto #{idx + 1}: {photo.caption || photo.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
